import { logEvent } from "../../../telemetry";

const DEFERRED_WRITES_CONNECTOR = "deckorate";

/**
 * Collects Office.js property-write closures so a bulk operation spanning
 * many shapes/slides/colour-targets can defer every mutation until one
 * final `context.sync()`.
 *
 * Office.js hosts (PowerPoint included) record a new native-undo entry
 * every time `context.sync()` flushes a batch that contains at least one
 * write. A bulk action that reads then writes+syncs slide-by-slide (or
 * colour-type-by-colour-type) therefore fragments into one undo step per
 * sync instead of a single "Ctrl+Z undoes the whole thing" step — reads
 * can still sync as often as needed (no undo entry is created when nothing
 * was mutated), but every write must stay queued here until the operation
 * is completely done, then flush() once right before the one sync() that
 * commits it.
 */
export class DeferredWrites {
  private writes: Array<{ reason: string; write: () => void }> = [];

  /** `reason` identifies this write in telemetry if it throws during
   * flush() — pass a short, stable tag (e.g. "shape-fill-apply"). */
  queue(reason: string, write: () => void): void {
    this.writes.push({ reason, write });
  }

  /** Executes every queued write, in order, then clears the queue. A write
   * that throws is skipped (logged, not silently dropped) rather than
   * aborting the rest — matches this codebase's "one bad shape can't abort
   * the batch" convention. Returns the number of writes that failed —
   * callers that count "how many succeeded" as writes are queued (before
   * flush ever runs) must subtract this from that running total, since a
   * queued write's own success isn't known until it actually executes here. */
  flush(): number {
    const pending = this.writes;
    this.writes = [];
    let failed = 0;
    for (const { reason, write } of pending) {
      try {
        write();
      } catch {
        failed++;
        logEvent("connector.action.invoke", "warn", { connectorSystemName: DEFERRED_WRITES_CONNECTOR, reason });
      }
    }
    return failed;
  }
}
